import React, { useState } from "react";
import { Typography, Row, Col, Divider, Button, List, Card, Input, Checkbox, Image, Radio } from "antd";
import type { StepProps } from "../index";
import DetailItem from "../../../components/DetailItem";
import { useHistory } from "react-router-dom";
import { EEmployeeRole, EEmployeeStatus } from "../../../types/employee.type";
import useFetchList from "../../../hooks/useFetchList";
import { SearchOutlined } from "@ant-design/icons";
import { renderRoles } from "../../../helpers/renderRoles";
import CustomPagination from "../../../components/CustomPagination";
import { ListUserQuery, UserProperties } from "../../../types/user.type";
import NotSet from "../../../components/NotSet";
import useAuth from "../../../hooks/useAuth";
import OfficePortalConnection from "../components/OfficePortalConnection";
import { EApprovalStatus } from "../../../types/approval.type";
import { EOfficeStatus } from "../../../types/office.type";

const { Text, Title } = Typography;
const DetailOfficeInformation: React.FC<StepProps> = ({
	officeId,
	office,
	FOList,
	BOList,
	slug,
	type,
	doneSyncPortal,
	isSimpleDetail,
}) => {
	const history = useHistory();
	const { isAdminHQPlus } = useAuth();

	const { isLoading, data, pagination, search, setSearch, changePage } = useFetchList<UserProperties, ListUserQuery>({
		endpoint: "users",
		limit: 12,
		fetchable: type !== "approval",
		initialQuery: {
			includeData: "listingCount",
			filterStatus: EEmployeeStatus.ACTIVE,
			filterOfficeId: officeId,
			filterRole: "agent",
			filterRoleApprovalStatus: "APPROVED",
		},
	});

	const renderAgent = (item: UserProperties) => {
		let roles = item.employee?.[0]?.employeeRole?.map((item) => item.role) ?? [];
		let additionalRole = renderRoles([...roles], [EEmployeeRole.franchise_owner, EEmployeeRole.business_owner]);
		return (
			<List.Item>
				<Card
					className="card-user"
					bordered={false}
					size="small"
					cover={
						<img
							className="card-user-photo"
							alt={item?.nickName + (additionalRole ? "(" + additionalRole + ")" : "")}
							src={item?.profilePic ?? "/images/no-preview.jpg"}
						/>
					}
				>
					<Card.Meta
						title={item?.nickName?.toUpperCase()}
						description={`${item.listingCount} Listing`}
					/>
				</Card>
			</List.Item>
		);
	};

	return (
		<React.Fragment>
			<Row gutter={[24, 24]}>
				<Col
					xs={{ span: 24 }}
					lg={{ span: 6 }}
				>
					{office?.logo ? (
						<div>
							<Image
								width={200}
								src={office?.logo?.fileUrl}
								alt={office?.officeName}
								preview={false}
								fallback="/images/fallback-logo.png"
							/>
						</div>
					) : (
						<React.Fragment></React.Fragment>
					)}
				</Col>

				<Col
					xs={{ span: 24 }}
					lg={{ span: 18 }}
				>
					<Row
						gutter={[24, 0]}
						className="detail-bordered"
					>
						{isAdminHQPlus && (
							<Col
								span={24}
								className="no-border"
							>
								<DetailItem label="Allow Similar Listing">
									<Radio.Group value={office?.denyDuplicateListing}>
										<Radio value={false}>Allow</Radio>
										<Radio value={true}>Deny</Radio>
									</Radio.Group>
								</DetailItem>
							</Col>
						)}
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem label="Franchise Owner">
								{FOList.length > 0 ? (
									<Text ellipsis={true}>
										{FOList.map((item) => item.user?.nickName?.toUpperCase()).join(", ")}
									</Text>
								) : (
									<NotSet />
								)}
							</DetailItem>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem label="Business Owner">
								{BOList.length > 0 ? (
									<Text ellipsis={true}>
										{BOList.map((item) => item.user?.nickName?.toUpperCase()).join(", ")}
									</Text>
								) : (
									<NotSet />
								)}
							</DetailItem>
						</Col>

						<Col span={24}>
							<DetailItem
								label="Office Name"
								value={office?.officeName}
							/>
						</Col>
						{/* {office?.subdomain ? (
							<Col
								xs={{ span: 24 }}
								md={{ span: 12 }}
							>
								<DetailItem
									label="Sub Domain"
									onClick={copyToClipboard}
								>
									{office?.subdomain}
									<span style={{ color: "#8C8C8C" }}>
										.{process.env.REACT_APP_WEBSITE_URL || "remax.co.id"}
									</span>{" "}
									<CopyOutlined
										style={{
											color: "#9FACBF",
										}}
									/>
								</DetailItem>
							</Col>
						) : (
							<React.Fragment></React.Fragment>
						)} */}

						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="Phone Number 1"
								value={office?.firstPhoneNumber}
							/>
						</Col>
						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="Phone Number 2"
								value={office?.secondPhoneNumber}
							/>
						</Col>

						<Col span={24}>
							<DetailItem
								label="Office Email"
								value={office?.email}
							/>
						</Col>

						<Col
							xs={{ span: 24 }}
							md={{ span: 12 }}
						>
							<DetailItem
								label="Province"
								value={office?.province}
							/>
						</Col>
						<Col
							xs={{ span: 12 }}
							md={{ span: 24 }}
						>
							<DetailItem
								label="City"
								value={office?.city}
							/>
						</Col>
						<Col
							xs={{ span: 12 }}
							md={{ span: 24 }}
						>
							<DetailItem
								label="Postal Code"
								value={office?.postalCode}
							/>
						</Col>

						<Col span={24}>
							<DetailItem
								label="Address"
								value={office?.address}
							/>
						</Col>
						<Col span={24}>
							<Checkbox
								disabled={true}
								checked={office?.isOfficePkp}
								className="color-not-disabled"
							>
								Office PKP
							</Checkbox>
						</Col>
						{office &&
							office?.approvalStatus === EApprovalStatus.APPROVED &&
							office?.status !== EOfficeStatus.OPEN && (
								<Col span={24}>
									<OfficePortalConnection
										officeId={office?.officeId}
										doneSyncPortal={doneSyncPortal}
									/>
								</Col>
							)}
					</Row>
				</Col>
			</Row>
			{type !== "approval" ? (
				<React.Fragment>
					<Row
						gutter={[24, 0]}
						style={{ marginTop: 40, marginBottom: 20 }}
					>
						<Col
							xs={{ span: 12 }}
							md={{ span: 18 }}
						>
							<Title level={4}>List Agent</Title>
						</Col>
						<Col
							xs={{ span: 12 }}
							md={{ span: 6 }}
							style={{ textAlign: "right" }}
						>
							<Input
								value={search}
								className="outer-shadow"
								placeholder="Search"
								suffix={<SearchOutlined />}
								allowClear
								onChange={(e) => setSearch(e.target.value)}
							/>
						</Col>
					</Row>
					<List
						loading={isLoading}
						grid={{ gutter: 16, xs: 2, md: 3, lg: 4, xl: 5, xxl: 6 }}
						dataSource={data}
						renderItem={renderAgent}
					/>
					<Divider style={{ margin: "5px 0" }} />
					<CustomPagination
						current={pagination.page}
						total={pagination.totalData}
						defaultPageSize={pagination.perPage}
						pageSize={pagination.perPage}
						onChange={changePage}
					/>
				</React.Fragment>
			) : (
				<React.Fragment></React.Fragment>
			)}
			{!isSimpleDetail && (
				<Row gutter={[24, 0]}>
					<Col
						span={24}
						style={{ height: 20 }}
					></Col>
					<Col span={12}>
						<Button
							disabled
							className="btn-fixed-lg"
						>
							Previous
						</Button>
					</Col>
					<Col
						span={12}
						style={{ textAlign: "right" }}
					>
						<Button
							type="primary"
							className="btn-fixed-lg"
							onClick={() => history.push("/" + slug + "/" + officeId + "/detail/setting-web")}
						>
							Next
						</Button>
					</Col>
				</Row>
			)}
		</React.Fragment>
	);
};
export default DetailOfficeInformation;
